/* eslint-disable no-mixed-operators */
/**
 * @module cercaliagl/util
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const result = cercaliagl.util.functionName(arg1, arg2);
 * ```
 */

/**
 * @typedef {Object} GeometryGeoJSON
 * @property {string} type Geometry type
 * @property {Array.<number>|Array.<Array.<number>>|Array.<Array.<Array.<number>>>} coordinates
 */

import maplibregl from 'maplibre-gl';
import { parse as wktParse, stringify as wktStringify } from 'wellknown';
import buffer from '@turf/buffer';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import polygonToLine from '@turf/polygon-to-line';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import turfLinestring from 'turf-linestring';
import turfAlong from '@turf/along';
import lineSlice from '@turf/line-slice';
import lineIntersect from '@turf/line-intersect';
import turfLength from '@turf/length';
import findIntersections from 'sweepline-intersections';
import tokml from 'tokml';
import turfPolygon from 'turf-polygon';
import turfPoint from 'turf-point';
// eslint-disable-next-line no-unused-vars
import LineString from 'ol/geom/LineString';
// eslint-disable-next-line no-unused-vars
import Polygon from 'ol/geom/Polygon';
import { WKT } from 'ol/format';
import GeometryType from './GeometryType';
import LonLat from './LonLat';
import { CountriesPortalBefore } from './globals';

let uid = 0;

/**
 * @return {boolean} `true` if is mobile else `false`.
 */
export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * @return {string}
 */
export function createUid() {
  uid += 1;
  return `${uid}`;
}

/**
 * Convert degrees to radiants.
 * @api
 * @param {number} angle Angle in degrees
 * @return {number} Angle in radiants.
 */
export function toRadians(angle) {
  return (angle * Math.PI) / 180;
}

/**
 * Create circle geometry given a `center`, a radius and optionally number of `vertices`.
 * @api
 * @param {LonLat} center Center.
 * @param {number} radius Radius in meters
 * @param {number} [vertices] Number of vertices. Default `64`.
 * @return {import("./util").GeometryGeoJSON} geometry
 */
export function createCircleGeometry(center, radius, vertices = 64) {
  const radiusInKm = radius / 1000;

  const ret = [];
  const distanceX = radiusInKm / (111.32 * Math.cos((center.getLat() * Math.PI) / 180));
  const distanceY = radiusInKm / 110.574;

  let theta;
  let x;
  let y;
  for (let i = 0; i < vertices; i += 1) {
    theta = (i / vertices) * (2 * Math.PI);
    x = distanceX * Math.cos(theta);
    y = distanceY * Math.sin(theta);
    ret.push([center.getLon() + x, center.getLat() + y]);
  }
  ret.push(ret[0]);

  return {
    type: 'Polygon',
    coordinates: [ret],
  };
}

/**
 * @api
 * @param {Array.<number>} coordA Position 1
 * @param {Array.<number>} coordB Position 2
 * @return {number} Distance
 */
export function distanceBetweenPoints(coordA, coordB) {
  let pos1;
  let pos2;
  if (coordA instanceof LonLat && coordB instanceof LonLat) {
    pos1 = [coordA.getLon(), coordA.getLat()];
    pos2 = [coordB.getLon(), coordB.getLat()];
  } else {
    pos1 = coordA;
    pos2 = coordB;
  }
  const geometryPointsCalculated = { type: GeometryType.LINESTRING, coordinates: [pos1, pos2] };
  const currentDistance = turfLength(geometryPointsCalculated, { units: 'kilometers' }) * 1000;
  const finalDistance = Math.round(currentDistance * 100) / 100;
  return finalDistance;
}

/**
 * Calculation distance between two coordinates.
 * @param {LonLat} coordA Coordinate 1,
 * @param {LonLat} coordB Coordinate 2.
 * @return {number} distance between two coordinates.
 * @api
 */
export function distanceTo(coordA, coordB) {
  const pointA = coordA.toArray();
  const pointB = coordB.toArray();
  const R = 6371;
  const o1 = toRadians(pointA[1]);
  const fi1 = toRadians(pointA[0]);
  const o2 = toRadians(pointB[1]);
  const fi2 = toRadians(pointB[0]);
  const deltaO = o2 - o1;
  const deltaFi = fi2 - fi1;
  const a = Math.sin(deltaO / 2) * Math.sin(deltaO / 2) + Math.cos(o1) * Math.cos(o2) * Math.sin(deltaFi / 2) * Math.sin(deltaFi / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d * 1000;
}

/**
 * Convert a `WKT` to a coordinates string.
 * @param {string} wkt WKT (https://es.wikipedia.org/wiki/Well_Known_Text)
 * @return {Array.<Array.<number>>} path Path.
 */
export function wktToArrayCoordinates(wkt) {
  let i;
  let j;
  let lat;
  let lng;
  let tmp;
  let tmpArr;
  let lngLats = [];
  const m = wkt.match(/\([^\(\)]+\)/g); // eslint-disable-line no-useless-escape
  if (m !== null) {
    for (i = 0; i < m.length; i += 1) {
      // match all numeric strings
      tmp = m[i].match(/-?\d+\.?\d*/g);
      if (tmp !== null) {
        for (j = 0, tmpArr = []; j < tmp.length; j += 2) {
          lng = Number(tmp[j]);
          lat = Number(tmp[j + 1]);
          tmpArr.push([lng, lat]);
        }
        lngLats = lngLats.concat(tmpArr);
      }
    }
  }
  return lngLats;
}

/**
 * Get filename by URL.
 * @param {string} url Url
 * @return {filename} Filename
 */
export function getFilename(url) {
  const filename = url.substring(url.lastIndexOf('/') + 1);
  return filename.substring(0, filename.lastIndexOf('.'));
}

/**
 * Return if url is on base64 format
 * @param {string} url Url
 * @return {boolean} True if url is on base64 format, false otherwise
 */
export function isOnBase64(url) {
  const regex = /data:image\/[bmp,gif,ico,jpg,png,svg,webp,x\-icon,svg+xml]+;base64,[a-zA-Z0-9,+,/]+={0,2}/gm;
  return url.match(regex);
}

/**
 * Count number of words from a string.
 * @param {string} str String
 * @return {number} Number of words.
 */
export function countWords(str) {
  const matches = str.match(/[\w\d]+/gi);
  return matches ? matches.length : 0;
}

/**
 * @private
 * @param {number} degrees Degrees
 * @param {number} minutes Minutes
 * @param {number} seconds Seconds
 * @param {string} direction Direction
 * @return {number} dd
 */
function convertDMSToDD(degrees, minutes, seconds, direction) {
  let dd = degrees + (minutes / 60) + (seconds / 3600);
  if (direction === 'S' || (direction === 'W' || direction === 'O')) { // South / Sud or West / Oeste
    dd *= -1;
  }
  return dd;
}

/**
 * Convert degrees minutes seconds to decimal format.
 * @param {string} input Input string. Format examples: `40° 03' 49.3" N, 3° 24' 42.2" W`, `40°03'49"N 3°24'42" W`
 * @return {string} Decimal format. Example `41.431,2.41431`
 * @api
 */
export function convertDmsToDecimal(input) {
  const patternCoordinatesDMS = /^([0-9]{1,2})[:|°|º][\s+]?([0-9]{1,2})[:|'|′]?[\s+]?([0-9]{1,2}(?:\.[0-9]+){0,1})?["|″][\s+]?([N|S])[,|\s+][\s+]?([0-9]{1,3})[:|°|º][\s+]?([0-9]{1,2})[:|'|′]?[\s+]?([0-9]{1,2}(?:\.[0-9]+){0,1})?["|″][\s+]?([E|W])$/;
  const parts = patternCoordinatesDMS.exec(input);
  const lat = convertDMSToDD(parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3]), parts[4]);
  const lng = convertDMSToDD(parseFloat(parts[5]), parseFloat(parts[6]), parseFloat(parts[7]), parts[8]);
  const str = `${lat},${lng}`;
  return str;
}

/**
 * Get formatted adrress from proximity server data response.
 * @param {import('./servers').ProximityResult} data Proximity result
 * @return {string} Formatted address.
 * @api
 */
export function getProximityFormattedAddress(data) {
  const { proximity } = data.cercalia;
  const { ge } = proximity.gelist;
  let text;
  if (proximity.type === 'adr') {
    if (ge.housenumber !== undefined) {
      text = `${ge.name} ${ge.housenumber.value}, ${ge.municipality.value}`;
    } else {
      text = `${ge.name}, ${ge.municipality.value}`;
    }
  } else if (proximity.type === 'ct') {
    text = `${ge.name}, ${ge.subregion.value}`;
  } else {
    text = ge.name;
  }
  return /** @type {string} */ (text);
}

/**
 * Convert distance `metric` to `imperial`.
 * @param {number} distance Distance in meters.
 * @return {string} distanceImp Distance imperial format string.
 * @api
 */
export function distanceMetricToImperial(distance) {
  let distanceImp;
  if (distance > 1609.344) {
    distanceImp = `${(distance / 1609.344).toFixed(2)} mi`;
  } else if (distance > 3.28) {
    distanceImp = `${(distance * 3.28).toFixed(2)} ft`;
  } else {
    distanceImp = `${(distance * 39.37).toFixed(2)} in`;
  }
  return distanceImp;
}

/**
 * Convert area `metric` to `imperial`.
 * @param {number} area Area in ha
 * @return {string} Area in acrees.
 * @api
 */
export function areaMetricToImperial(area) {
  const areaImp = `${(area * 2.4711).toFixed(2)} ac`;
  return areaImp;
}

/**
 * Clone object
 * @param {Object} object
 * @return {Object} cloned object
 */
export function cloneDeep(object) {
  return JSON.parse(JSON.stringify(object));
}

/**
 * Transform WKT to KML format (https://developers.google.com/kml/documentation/kmlreference).
 * @param {string} wkt Geometry in WKT format.
 * @param {string=} opt_srs SRS system. Default: `Proj.EPSG4326`.
 * @return {string} KML object.
 * @api
 */
export function transformWktToKML(wkt) {
  const feature = wktParse(wkt);
  const result = tokml(feature);
  return result;
}

/**
 * Download XML file passing a XML string.
 * @param {string} strXML XML in string format
 * @param {string} [filename] Filename to download.
 * @api
 */
export function downloadXmlToFile(strXML, filename = `file_${Date.now()}.xml`) {
  const pom = document.createElement('a');
  const bb = new Blob([strXML], { type: 'text/xml' });

  pom.setAttribute('href', window.URL.createObjectURL(bb));
  pom.setAttribute('download', filename);

  pom.dataset.downloadurl = ['text/xml', pom.download, pom.href].join(':');
  pom.draggable = true;
  pom.classList.add('dragout');

  document.body.appendChild(pom);
  pom.click();
  document.body.removeChild(pom);
}

/**
 * SaveW route as KML.
 * @param {string} wkt WKT.
 * @param {string} [filename] Filename.
 * @api
 */
export function downloadKmlFromWkt(wkt, filename = `cercalia_route${Date.now()}.kml`) {
  const kml = transformWktToKML(wkt);
  downloadXmlToFile(kml, filename);
}

/**
 * @param {import('./servers').GeEntity} ge GeEntity.
 * @param {string} type Ge Type.
 * @return {string} Formatted address.
 */
export function formatAddress(ge, type) {
  let text = '';
  const dist = ge.dist ? parseInt(ge.dist, 10) : -1;
  if (dist < 50000) {
    const postalcode = ge.postalcode ? ge.postalcode.id : null;
    const municipality = ge.municipality?.value;
    const locality = ge.city ? ge.city.value : null;
    const locationSelected = ge.name ? ge.name.value : null;
    if (type === 'ct') {
      text = (postalcode ? `${postalcode} ` : '')
        + (locationSelected || locality || (municipality || ''));
    } else if (['adr', 'st'].indexOf(type) !== -1 && ((!ge.km && dist > 100) || (ge.km && dist > 750))) {
      text = locationSelected || locality || (municipality || '');
    } else if (type === 'pc') {
      text = `${ge.name.value}, ${municipality}, ${ge.country.value}`;
    } else if (!ge.km) { // street
      if (ge.name && ge.name.value) {
        text = ge.name.value;
      } else if (ge.country.id && CountriesPortalBefore.indexOf(ge.country.id) !== -1) {
        text = (ge.housenumber ? `${ge.housenumber.value} ` : '') + ge.name;
      } else {
        text = ge.name + (ge.housenumber ? `, ${ge.housenumber.value}` : '');
      }
    } else { // road
      text = ge.name + (ge.km ? `, km ${ge.km.value}` : '');
    }
  }
  return text;
}

/**
 * @param {string} time Time format `hh:mm:ss`.
 * @return {number} Seconds.
 */
export function hhmmssToSeconds(time) {
  const a = time.split(':');
  return +a[0] * 60 * 60 + +a[1] * 60 + +a[2];
}

/**
 * @param {string} time Time format `hh:mm:ss`.
 * @param {boolean} oneMinuteMinimum add 1 minute if time is zero.
 * @return {string} Readable time.
 */
export function normalizeTime(time, oneMinuteMinimum = true) {
  const arrTime = time.split(':');
  let str = '';
  let hours = Number(arrTime[0]) > 0 ? `${Number(arrTime[0])}h ` : '';
  let minutes = Number(arrTime[1]);
  if (Number(arrTime[2]) > 30) {
    minutes += 1;
  }
  if (minutes === 0 && oneMinuteMinimum) {
    minutes += 1;
  }
  if (minutes === 60) {
    minutes = 0;
    hours += 1;
  }
  str += `${hours}${minutes} min`;
  return str;
}

/**
 * @param {string} dist Distance.
 * @return {string} Distance normalized.
 */
export function normalizeDistance(dist) {
  const ndist = parseFloat(Number(dist).toFixed(1));
  if (ndist < 1) {
    return `${ndist * 1000}m`;
  }
  return `${ndist}Km`;
}

/**
 * @param {string} dist Distance in meters
 * @return {string} Distance
 */
export function normalizeDistMeters(dist) {
  const nDist = parseFloat(dist);
  if (nDist > 1000) {
    return `${(nDist / 1000).toFixed(2)}km`;
  }
  return `${nDist}m`;
}

/**
 * WKT to Geometry.
 * @param {string} wkt WKT - http://es.wikipedia.org/wiki/Well_Known_Text
 * @return {GeoJSON} GeoJSON - https://geojson.org/
 * @api
 */
export function wktToGeometry(wkt) {
  return wktParse(wkt);
}

/**
 * Transform GeoJSON format to WKT.
 * @param {Object} geojson GeoJSON - https://geojson.org/
 * @return {string} WKT - http://es.wikipedia.org/wiki/Well_Known_Text
 * @api
 */
export function geojsonToWKT(geojson) {
  return wktStringify(geojson);
}

/**
 * Transform linestring to polygon adding a buffer.
 * @param {string} wkt WKT - http://es.wikipedia.org/wiki/Well_Known_Text
 * @param {number} [radius] Radius. Default `10`.
 * @api
 */
export function wktLinestringToPolygonBuffered(wkt, radius = 10) {
  const geojson = wktParse(wkt);
  const polygon = buffer(geojson, radius, { units: 'meters' });
  return wktStringify(polygon);
}

/**
 * Get closest position of a feature given a coordinate (position)
 * @param {LonLat} position Position
 * @param {import("./Feature").default} feature Feature
 * @return {LonLat} Coordinate of closest feature
 * @api
 */
export function getClosestLonLatOfFeature(position, feature) {
  const geojson = [GeometryType.LINESTRING, GeometryType.MULTILINESTRING].indexOf(feature.getGeometryType()) !== -1
    ? feature.getGeoJSON() : polygonToLine(feature.getGeoJSON());
  const nearest = nearestPointOnLine(geojson, position.toArray());
  const coordinate = nearest.geometry.coordinates;
  return new LonLat(coordinate[0], coordinate[1]);
}

/**
 * Get closest feature by a position given a position and a list of features.
 * @param {LonLat} position Position
 * @param {Array.<import("./Feature").default>} features Features
 * @return {import("./Feature").default} Feature
 * @api
 */
export function getClosestFeatureByPosition(position, features) {
  let minDistance = Infinity;
  let closestFeature = null;
  features.forEach((feature) => {
    const lonLatClosest = getClosestLonLatOfFeature(position, feature);
    const distance = distanceTo(position, lonLatClosest);
    if (distance < minDistance) {
      minDistance = distance;
      closestFeature = feature;
    }
  });
  return closestFeature;
}

/**
 * Returns `true` if the coordinate is inside polygon. Polygon must be in WKT format.
 *
 * @param {string} polygon Polygon WKT string
 * @param {LonLat} lonLat Coordinate
 * @return {boolean} Return `true` if position is inside polygon.
 * @api
 */
export function geofencing(polygon, lonLat) {
  if (polygon.indexOf('POLYGON') >= 0) {
    const geometry = wktToGeometry(polygon);
    const coord = [lonLat.getLon(), lonLat.getLat()];
    return booleanPointInPolygon(coord, geometry);
  }
  return false;
}

/**
 * @param {string} element HTML string
 * @position {LonLat} position Position
 * @return {maplibregl.Marker} Marker
 * @api
*/
export function createMaplibreSimpleMarker(element, position) {
  const marker = new maplibregl.Marker(element).setLngLat(position.toMapboxLngLat());
  return marker;
}

export function donutSegment(start, end, r, r0, color) {
  // eslint-disable-next-line no-param-reassign
  if (end - start === 1) end -= 0.00001;
  const a0 = 2 * Math.PI * (start - 0.25);
  const a1 = 2 * Math.PI * (end - 0.25);
  const x0 = Math.cos(a0);
  const y0 = Math.sin(a0);
  const x1 = Math.cos(a1);
  const y1 = Math.sin(a1);
  const largeArc = end - start > 0.5 ? 1 : 0;

  // draw an SVG path
  return `<path d="M ${r + r0 * x0} ${r + r0 * y0} L ${r + r * x0} ${
    r + r * y0
  } A ${r} ${r} 0 ${largeArc} 1 ${r + r * x1} ${r + r * y1} L ${
    r + r0 * x1
  } ${r + r0 * y1} A ${r0} ${r0} 0 ${largeArc} 0 ${r + r0 * x0} ${
    r + r0 * y0
  }" fill="${color}" />`;
}

export function createDonutChart(props, propsNames, colors) {
  const offsets = [];
  const counts = [];
  propsNames.forEach((propName) => {
    counts.push(props[propName]);
  });
  let total = 0;
  counts.forEach((count) => {
    offsets.push(total);
    total += count;
  });

  const fontSize = total >= 1000 ? 22 : total >= 100 ? 20 : total >= 10 ? 18 : 16;
  const r = total >= 1000 ? 50 : total >= 100 ? 32 : total >= 10 ? 24 : 18;
  const r0 = Math.round(r * 0.6);
  const w = r * 2;
  let html = `<div>
  <svg width="${w}" height="${w}" viewbox="0 0 ${w} ${w}" text-anchor="middle" style="font: ${fontSize}px sans-serif; display: block">`;

  for (let i = 0; i < counts.length; i += 1) {
    html += donutSegment(
      offsets[i] / total,
      (offsets[i] + counts[i]) / total,
      r,
      r0,
      colors[i],
    );
  }
  html += `<circle cx="${r}" cy="${r}" r="${r0}" fill="white" />
  <text dominant-baseline="central" transform="translate(${r}, ${r})">
  ${total.toLocaleString()}
  </text>
  </svg>
  </div>`;

  const el = document.createElement('div');
  el.innerHTML = html;
  return el.firstChild;
}

/**
 * @param {string} text Text
 * @return {string} Text without HTML tags
 */
export function textRemoveHTMLTags(text) {
  if (text) {
    return text.replace(/<b>|<\/b>/g, '');
  }
  return null;
}

/**
 * @param {string|number} value Value
 * @return {boolean}
 */
export function isNumber(value) {
  return !Number.isNaN(Number(value));
}

/**
   * @private
   * @param {Array<any>} array Array where search
   * @param {number} valueToSearch Value to search his position
   * @param {number} low Start search position (min 0)
   * @param {number} high final search position (max array.length -1)
   */
export function binarySearchZIndex(array, valueToSearch, low, high) {
  let currentLow = low;
  let currentHigh = high;
  while (currentLow < currentHigh) {
    const mid = Math.floor(currentLow + (currentHigh - currentLow) / 2);
    if (valueToSearch <= array[mid].properties.zIndex) currentHigh = mid;
    else currentLow = mid + 1;
  }
  return valueToSearch >= array[currentLow].properties.zIndex ? currentLow + 1 : currentLow;
}

/**
 * @param {GeoJSONFeature} feature Polygon geojson feature
 * @return {boolean} Return `true` if a polygon feature has intersections
 * @api
*/
export function checkPolygonSegmentIntersection(feature) {
  const intersection = findIntersections(feature);
  return intersection?.length > 0;
}

/**
 * Get the intersection points between a segment and a polygon
 * @param {LineString} segment Segment geojson feature
 * @param {Polygon} poly Polygon geojson feature
 * @return {FeatureCollection<LineString>} Return a feature collection with the intersection points
 * @private
 */
function getPolygonIntersectionPointsWithASegment(segment, poly) {
  const intersection = lineIntersect(segment, poly); // Trobem els punts d'intersecció entre el poligon i la linia
  return !intersection ? [] : intersection.features;
}

/**
 * Must be passed to sort function to order an array of coordinates by distance to a reference point
 * @param {Array.<number>} referenceCoords Reference coordinates
 * @private
 */
function orderArrayByDistanceToPoint(referenceCoords) {
  return (coordA, coordB) => {
    const distancePointToA = distanceBetweenPoints(referenceCoords, coordA);
    const distancePointToB = distanceBetweenPoints(referenceCoords, coordB);

    if (distancePointToA < distancePointToB) {
      return -1;
    } if (distancePointToA > distancePointToB) {
      return 1;
    } return 0;
  };
}

/**
 * Find intersections between a polygon and a linestring
 * @param {import("./Feature").default} polygonFeature Polygon geojson feature 1
 * @param {import("./Feature").default} lineFeature Linestring geojson feature 2
 * @return {Array<GeoJSONFeature>} Return intersection feature
 * @api
*/
export function getLinestringIntersectionsWithPolygon(polygonFeature, lineFeature) {
  const poly = turfPolygon(polygonFeature.getGeoJSON().geometry.coordinates);
  const line = turfLinestring(lineFeature.getGeoJSON().geometry.coordinates);
  const segments = [];
  // Obtenim els segments de la linea
  for (let i = 0; i < line.geometry.coordinates.length - 1; i += 1) {
    segments.push(turfLinestring([line.geometry.coordinates[i], line.geometry.coordinates[i + 1]]));
  }
  const linestringsInside = [];
  segments.forEach((segment) => {
    const intersection = getPolygonIntersectionPointsWithASegment(segment, poly);
    const startPoint = segment.geometry.coordinates[0];
    const finalPoint = segment.geometry.coordinates[segment.geometry.coordinates.length - 1];
    const startPointInside = booleanPointInPolygon(turfPoint(startPoint), poly);
    const finalPointInside = booleanPointInPolygon(turfPoint(finalPoint), poly);
    // Comprovar si el segment creua el polígon
    if (intersection || startPointInside || finalPointInside) {
      const pointsInside = [];
      segment.geometry.coordinates.forEach((point) => { // Busquem els punts que estiguin dins del poligon
        const createdPoint = turfPoint(point);
        const inside = booleanPointInPolygon(createdPoint, poly);
        if (inside) {
          pointsInside.push(point);
        }
      });
      if (pointsInside.length === 0 && !intersection) {
        return;
      }
      if (intersection.length === 1) { // Si hi ha un punt d'intersecció vol dir que comença o acaba dins del polígon
        if (!startPointInside) {
          pointsInside.unshift(intersection[0].geometry.coordinates); // Si comença fora, afegim el punt d'intersecció al principi
        } else {
          pointsInside.push(intersection[0].geometry.coordinates); // Si acaba fora, afegim el punt d'intersecció al final
        }
      }
      if (intersection.length === 2) { // Si hi ha més d'un punt d'intersecció, afegim el primer i l'últim, ja que el segment creua el polígon
        intersection.sort(orderArrayByDistanceToPoint(startPoint)); // Ordenem els punts d'intersecció per distància al punt inicial del segment (de més a menys), ja que la llibreria ens ho dona desordenat
        if (!startPointInside) {
          pointsInside.unshift(intersection[0].geometry.coordinates);
          pointsInside.push(intersection[intersection.length - 1].geometry.coordinates);
        } else {
          linestringsInside.push(turfLinestring([startPoint, intersection[0].geometry.coordinates]));
          linestringsInside.push(turfLinestring([intersection[1].geometry.coordinates, finalPoint]));
          return;
        }
      }
      if (intersection.length > 2) {
        intersection.sort(orderArrayByDistanceToPoint(startPoint)); // Ordenem els punts d'intersecció per distància al punt inicial del segment (de més a menys), ja que la llibreria ens ho dona desordenat
        let i = 0;
        if (startPointInside) {
          linestringsInside.push(turfLinestring([startPoint, intersection[0].geometry.coordinates]));
          i = 1;
        }
        for (i; i < intersection.length - 1; i += 2) {
          linestringsInside.push(turfLinestring([intersection[i].geometry.coordinates, intersection[i + 1].geometry.coordinates]));
          linestringsInside.push(turfLinestring([intersection[i].geometry.coordinates, intersection[i + 1].geometry.coordinates]));
        }
        if (finalPointInside) {
          linestringsInside.push(turfLinestring([intersection[intersection.length - 1].geometry.coordinates, finalPoint]));
        }
        return;
      }
      if (pointsInside.length > 0) { // Si hem obtingut algun punt, creem un linestring amb els nous punts
        linestringsInside.push(turfLinestring(pointsInside));
      }
    }
  });
  if (linestringsInside.length === 0) return [];
  return linestringsInside;
}

/**
 * It obtains a partial line in WKT format specifying minimum and maximum percentages in base 1.
 * @api
 * @param {import("./Feature").default} feature Feature
 * @param {number} startfraction Min fraction `[0..1]`
 * @param {number} endfraction Max fraction `[0..1]`
 * @return {string|null} WKT
 */
export function getPartialLinestring(feature, startfraction, endfraction) {
  const lineString = feature.getGeoJSON();
  const startPoint = turfAlong(lineString, startfraction * turfLength(lineString));
  const endPoint = turfAlong(lineString, endfraction * turfLength(lineString));
  const subLineString = lineSlice(startPoint, endPoint, lineString);
  return wktStringify(subLineString);
}

/**
 * Transform WKT string to another projection.
 * @param {string} wkt WKT http://es.wikipedia.org/wiki/Well_Known_Text
 * @param {string} projectionSrc Projection WKT origin
 * @param {string} projectionDst Projection WKT destination
 * @return {string} Geometry in WKT format transformed.
 * @api
 */
export function transformWKT(wkt, projectionSrc, projectionDst) {
  const formatter = new WKT();
  const geometry = formatter.readGeometry(wkt);
  geometry.transform(projectionSrc, projectionDst);
  return formatter.writeGeometry(geometry);
}

/**
  * @params {string} anchor Anchor
  * @params {number} width Width
  * @params {number} height Height
  * @params {number} scale Scale
  * @api
  * @return {Array<number>} Anchor equivalent
*/
export function getAnchorStringEquivalent(anchor, width, height, scale = 1) {
  const widthScaled = width * scale;
  const heightScaled = height * scale;
  if (widthScaled === undefined || heightScaled === undefined) {
    return [0, 0];
  }
  switch (anchor) {
    case 'bottom': return [widthScaled / 2, heightScaled];
    case 'bottom-left': return [0, heightScaled];
    case 'bottom-right': return [widthScaled, heightScaled];
    case 'center': return [widthScaled / 2, heightScaled / 2];
    case 'left': return [0, heightScaled / 2];
    case 'right': return [widthScaled, heightScaled / 2];
    case 'top': return [widthScaled / 2, 0];
    case 'top-left': return [0, 0];
    case 'top-right': return [widthScaled, 0];
    default: return [0, 0];
  }
}
